import { Stepper } from '@mm/components';
import { AngleLeftLightIcon } from '@mm/icons';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { subscriptionTabsSelector } from '@mm/state/selectors/subscriptionSelectors';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import sadEmoji from 'assets/emojis/sad.png';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../common/components/TextInput';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import './CancellationPanel.pcss';
import {
  CancelQuestionary,
  initialPollValues,
  questionaryValidator,
} from './CancelQuestionary/CancelQuestionary';
import { MigrateToInstachampBanner } from './MigrateToInstachampBanner/MigrateToInstachampBanner';

const steps = [{ label: 'Step 1' }, { label: 'Step 2' }, { label: 'Step 3' }];

export const CancellationPanel = () => {
  const [stepperIdx, setStepperIdx] = useState(0);
  const [confirmText, setConfirmText] = useState('');
  const modalActions = useModalActions();
  const history = useHistory();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const companyId = useSelector(selectActiveCompanyId);
  const subscriptionTabs = useSelector(subscriptionTabsSelector);
  const { tabId, pageId } = useParams<{ tabId: string; pageId: string }>();

  const activeTab = subscriptionTabs.find(t => t.id === tabId);
  const enhancedFacebookPage = activeTab?.pages?.find(
    p => p.id === Number(pageId),
  );

  const facebookPageId = enhancedFacebookPage?.id;
  const subscriptionId = enhancedFacebookPage?.subscription?.id;

  const pollFormik = useFormik({
    initialValues: initialPollValues,
    onSubmit: () => {
      // Note: needed now, since we submt in the next step
    },
    validationSchema: questionaryValidator,
  });

  if (!facebookPageId || !subscriptionId) {
    history.push(SettingsRoutes.billing.getLink());
    return null;
  }

  const openPauseModal = () => {
    // Note: this functionality is on hold till a later ticket with BE part is done.
    // After PMs decision on what we actually need.
    // modalActions.openSubscriptionPausingModal();
    modalActions.openGeneralTextModal({
      className: 'cancellation__pause-modal',
      header: 'Pause my subscription',
      message: (
        <div>
          Please contact support at{' '}
          <a href="mailto:cs@mobilemonkey.com">cs@mobilemonkey.com</a> to pause
          your subscription.
        </div>
      ),
      onClose: () => {
        history.push(SettingsRoutes.billing.getLink());
      },
      closeOverride: () => {
        modalActions.closeModal();
        history.push(SettingsRoutes.billing.getLink());
      },
    });
  };

  const handleBack = () => {
    if (stepperIdx === 0) {
      history.push(SettingsRoutes.billing.getLink());
      return;
    }
    setStepperIdx(stepperIdx - 1);
    setConfirmText('');
  };

  const cancelButtonText =
    stepperIdx === 0
      ? 'I want to Cancel my Subscription'
      : 'Proceed with cancellation';

  const handleCancel = async () => {
    if (stepperIdx === 2) {
      await subscriptionCRUDActions.downgrade(companyId, subscriptionId, {
        companyId,
        facebookPageId,
        leavingReason: 'Replaced with pollResults field',
        additionalInfo: '',
        pollResults: pollFormik.values,
      });
      modalActions.openGeneralTextModal({
        onClose: () => history.push(SettingsRoutes.billing.getLink()),
        closeOverride: () => {
          modalActions.closeModal();
          history.push(SettingsRoutes.billing.getLink());
        },
        header: (
          <div className="cancellation__note-after-cancelling--header">
            <div>
              <img src={sadEmoji} alt="" />
            </div>
            <div className="cancellation__note-after-cancelling--header-tittle">
              We are sorry to see you go
            </div>
          </div>
        ),
        message: (
          <div className="cancellation__note-after-cancelling--text">
            <div>You have 7 days to change your mind</div>
            <div>and reactivate subscription without loosing data.</div>
            <br />
            <div>
              Please contact support at{' '}
              <a href="mailto:cs@mobilemonkey.com">cs@mobilemonkey.com</a>
            </div>

            <div>to reactivate your subscription.</div>
          </div>
        ),
        className: 'cancellation__note-after-cancelling',
      });
      return;
    }
    setStepperIdx(stepperIdx + 1);
  };

  const isCancelDisabled =
    (stepperIdx === 1 && !(pollFormik.dirty && pollFormik.isValid)) ||
    (stepperIdx === 2 && confirmText.toUpperCase() !== 'CONFIRM');

  return (
    <div className="cancellation-container">
      <div className="cancellation-container__navigation">
        <div
          className="cancellation-container__navigation-back"
          onClick={handleBack}
        >
          <AngleLeftLightIcon />
          <div>Back</div>
        </div>
        <div className="cancellation-container__navigation-header">
          Cancel my Subscription
        </div>
      </div>
      {enhancedFacebookPage?.realFacebookPage && (
        <MigrateToInstachampBanner
          page={enhancedFacebookPage}
          subscriptionId={subscriptionId}
          companyId={companyId}
        />
      )}
      <div>
        <Stepper
          activeIdx={stepperIdx}
          steps={steps}
          className="cancellation-stepper"
          markFormerComplete
        />
      </div>
      {stepperIdx === 1 && (
        <FormikProvider value={pollFormik}>
          <CancelQuestionary />
        </FormikProvider>
      )}
      {stepperIdx === 2 && (
        <div className="cancellation__cancel-step">
          <div className="cancellation__cancel-step--title">
            Are you 100% sure?
          </div>
          <div className="cancellation__cancel-step--instructions">
            Please type CONFIRM in the box below to cancel your account
          </div>
          <TextInput
            className="cancellation__cancel-step--text-input"
            placeholder="Start typing..."
            onChange={e => setConfirmText(e.currentTarget.value)}
            value={confirmText}
          />
        </div>
      )}
      <div className="cancellation-buttons-container">
        <PrimaryButton
          color={ButtonColor.invertedRed}
          className="cancellation__cancel-button"
          onClick={handleCancel}
          disabled={isCancelDisabled}
          size="sm"
        >
          {cancelButtonText}
        </PrimaryButton>
        <PrimaryButton
          className="cancellation__pause-button"
          onClick={openPauseModal}
        >
          Pause my subscription
        </PrimaryButton>
      </div>
    </div>
  );
};
